import { type SpotlightRect } from '../hooks/useSpotlightPosition';

interface SpotlightOverlayProps {
  targetRect: SpotlightRect | null;
  /** Extra padding around the cutout in px */
  padding?: number;
  borderRadius?: number;
  /** Called when the backdrop (outside the cutout) is clicked */
  onBackdropClick?: () => void;
}

/**
 * Full-screen overlay with a transparent cutout around the target element.
 * Uses an SVG mask for a clean rounded-rectangle hole.
 */
export function SpotlightOverlay({
  targetRect,
  padding = 8,
  borderRadius = 8,
  onBackdropClick,
}: SpotlightOverlayProps) {
  // When no target, render a plain backdrop
  if (!targetRect) {
    return (
      <div
        className="fixed inset-0 z-[60] animate-onboarding-in"
        onClick={onBackdropClick}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>
    );
  }

  const cutout = {
    x: targetRect.x - padding,
    y: targetRect.y - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  return (
    <div
      className="fixed inset-0 z-[60] animate-onboarding-in"
      aria-hidden="true"
    >
      <svg className="absolute inset-0 h-full w-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={cutout.x}
              y={cutout.y}
              width={cutout.width}
              height={cutout.height}
              rx={borderRadius}
              ry={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#spotlight-mask)"
          onClick={onBackdropClick}
        />
      </svg>
    </div>
  );
}
